import {
  MINIMAP_COLOR_NONE,
  MINIMAP_COLOR_TABLE,
  MINIMAP_SIZE,
  type MinimapFrame
} from '@apiary/shared'

/**
 * Отрисовка мини-карты (L1, docs/08 §4) в 2D-canvas.
 *
 * Цвет клетки — цвет конкретного блока из сгенерированной таблицы
 * (`MINIMAP_COLOR_TABLE`, генератор `scripts/build-minimap-palette.mjs`), а не
 * одной из 11 категорий: иначе бетон, шерсть, терракота и кирпич красятся серым,
 * как камень, и постройки на карте не видно.
 *
 * Рельеф даёт яркость: клетка выше бота светлее, ниже — темнее, плюс подсветка
 * перепада с северным соседом (как у Xaero: обрывы и стены видно линией).
 */

/** «Нет данных»: клетка почти прозрачна, цвет виден только как тёмный намёк. */
const NO_DATA_COLOR: readonly [number, number, number] = [30, 34, 42]
/** Индекс за пределами таблицы (кадр от более новой версии) — нейтральный серый. */
const FALLBACK_COLOR: readonly [number, number, number] = [120, 120, 124]

/**
 * Границы яркости.
 *
 * Раньше коэффициент падал до `0.25`, и при цветах категорий это ещё сходило за
 * «тени», а с настоящими цветами блоков карта уходила в чёрное: бетон на 20
 * блоков ниже бота становился углём. Диапазон узкий сознательно — рельеф
 * читается, но цвет блока остаётся узнаваемым.
 */
const SHADE_MIN = 0.62
const SHADE_MAX = 1.28
/** ±48 блоков перепада — это ±0.19 яркости. */
const SHADE_PER_BLOCK = 1 / 256
/** Вклад перепада с северным соседом: контур стены без изменения цвета. */
const SLOPE_STEP = 0.08

/** Цвет по индексу клетки. Вынесен, чтобы им же красить легенду и тесты. */
export function minimapCellColor(index: number): readonly [number, number, number] {
  if (index === MINIMAP_COLOR_NONE) return NO_DATA_COLOR
  return MINIMAP_COLOR_TABLE[index] ?? FALLBACK_COLOR
}

/** Кадр → `ImageData` размером 64×64 (клиент масштабирует его до 128²). */
export function minimapImageData(
  frame: Pick<MinimapFrame, 'data'>,
  createImageData: (w: number, h: number) => ImageData
): ImageData {
  const image = createImageData(MINIMAP_SIZE, MINIMAP_SIZE)
  const pixels = image.data
  for (let cell = 0; cell < MINIMAP_SIZE * MINIMAP_SIZE; cell++) {
    const height = frame.data[cell * 2] ?? 0
    const colorIndex = frame.data[cell * 2 + 1] ?? 0
    const rgb = minimapCellColor(colorIndex)
    const shade = shadeOf(frame.data, cell, height, colorIndex)
    const offset = cell * 4
    pixels[offset] = clamp255((rgb[0] ?? 0) * shade)
    pixels[offset + 1] = clamp255((rgb[1] ?? 0) * shade)
    pixels[offset + 2] = clamp255((rgb[2] ?? 0) * shade)
    pixels[offset + 3] = colorIndex === MINIMAP_COLOR_NONE ? 40 : 255
  }
  return image
}

/**
 * Яркость клетки: `128` — уровень бота, дальше линейно по высоте, плюс шаг
 * вверх/вниз относительно соседа с севера. Северный сосед взят потому, что
 * клетки лежат строками (`z` снаружи), и он уже посчитан — рельеф стоит одно
 * чтение массива.
 */
function shadeOf(data: Uint8Array, cell: number, height: number, colorIndex: number): number {
  let shade = 1 + (height - 128) * SHADE_PER_BLOCK
  if (cell >= MINIMAP_SIZE && colorIndex !== MINIMAP_COLOR_NONE) {
    const north = cell - MINIMAP_SIZE
    if ((data[north * 2 + 1] ?? 0) !== MINIMAP_COLOR_NONE) {
      const delta = height - (data[north * 2] ?? 0)
      if (delta > 0) shade += SLOPE_STEP
      else if (delta < 0) shade -= SLOPE_STEP
    }
  }
  return clamp(shade, SHADE_MIN, SHADE_MAX)
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value))
}

function clamp255(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)))
}
